import { AuthService } from './../../../services/auth/auth.service';
import {
  Component,
  OnChanges,
  SimpleChanges,
  Input,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { OpacSettingsService } from '../../../services/opac-settings/opac-settings.service';
import { Observable } from 'rxjs';
import { LayoutService } from '../../../services/layout.service';
import { SharedService } from './../../../services/shared/shared.service';
import { map, startWith } from 'rxjs/operators';
import {
  MatLegacyAutocomplete as MatAutocomplete,
  MatLegacyAutocompleteTrigger as MatAutocompleteTrigger,
} from '@angular/material/legacy-autocomplete';
import { SidenavContent } from '../sidenav-content';

@Component({
  selector: 'ag-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent extends SidenavContent implements OnChanges {
  @ViewChild('libraryInput') libraryInput: ElementRef<HTMLInputElement>;
  @ViewChild('user') userName: ElementRef;
  @Input() lid: string;
  @ViewChild(MatAutocomplete) matAutocomplete: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  forgotPasswordForm: UntypedFormGroup;
  userNameLabel: string;
  showLibraryLookupBox = false;

  filteredLibraryOptions: Observable<string[]>;
  singleLibrary: boolean;

  private _libsArray: string[] = [];
  private _selectedLibraryWithId;
  private _fb = inject(UntypedFormBuilder);
  private _authService = inject(AuthService);
  private _opacService = inject(OpacSettingsService);
  private _sharedService = inject(SharedService);

  constructor() {
    super(inject(LayoutService));
    this.forgotPasswordForm = this._fb.group({
      userName: ['', Validators.required],
      lid: ['', Validators.required],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.lid) {
      this._bindDropdownLibName(changes.lid.currentValue);
      this._setupLibraryFiltering();
      this._getLoginInstructions();
    }

    if (this.userName) {
      this.userName.nativeElement.focus();
    }
  }

  submit() {
    if (this.showLibraryLookupBox) {
      // new way of doing with dropdown if it exists
      const formValue = this.forgotPasswordForm.value;
      const userName = formValue.userName.trim();
      const isUserNameValid = /\S/.test(userName);
      const isLibrarySelected = /\S/.test(this._selectedLibraryWithId);
      if (isUserNameValid && isLibrarySelected) {
        const [lid] = this._selectedLibraryWithId
          .split(':')
          .map((part) => part.trim().toLowerCase());
        const request = { userName, lid };
        this._forgotPasswordWithLidAsync(request);
      } else {
        this._sharedService.openSnackBar('Username and/or library ID is empty', 'ok');
      }
    } else {
      // old way of doing it
      this._submitForm(this.forgotPasswordForm);
    }
  }

  private async _forgotPasswordWithLidAsync(request_body) {
    this._authService.forgotPasswordWithLidAsync(request_body).subscribe((response) => {
      if (response !== null) {
        if (response.status == '200 OK' && response.message == 'Success') {
          this._sharedService.openSnackBar(response.data.msg, 'ok');
          this._closeNav();
        }
      } else {
        const messsage = 'Unable to save changes at the moment';
        this._sharedService.openSnackBar(messsage, 'ok');
      }
    });
  }

  private _getLoginInstructions(): void {
    this.userNameLabel = this._opacService.loginConfig.userNameLabel;
  }

  private _bindDropdownLibName(lid: string) {
    this._libsArray = this._sharedService.getLibDropdownList();

    this.singleLibrary = this._libsArray.length === 1;

    const library = this._libsArray.find(
      (lib) => lib.split(':')[0]?.trim().toLowerCase() === lid.toLowerCase(),
    );

    if (library) {
      this._selectedLibraryWithId = library;
      this.librariesControl.setValue(this._selectedLibraryWithId);
    }

    if (this._libsArray) {
      this.showLibraryLookupBox = true;
    }
  }

  private _setupLibraryFiltering() {
    this.filteredLibraryOptions = this.librariesControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        if (value === '' || typeof value === 'object') {
          return this._libsArray;
        } else {
          const filterValue = value.toLowerCase();
          const matchingOptions = this._libsArray.filter((option) =>
            option.toLowerCase().includes(filterValue),
          );
          if (matchingOptions.length === 0) {
            this.librariesControl.setErrors({ invalidOption: true });
          } else {
            this.librariesControl.setErrors(null);
          }
          return matchingOptions;
        }
      }),
    );
  }

  private _submitForm(data) {
    this._authService.forgotPassword(data.value).subscribe((response) => {
      if (response.status == '200 OK' && response.message == 'Success') {
        this._sharedService.openSnackBar(response.data.msg, 'ok');
        this._closeNav();
      } else if (response.status == '200 OK' && response.message == 'Fail') {
        this._sharedService.openSnackBar(response.data.msg, 'ok');
      }
    });
  }

  cancel() {
    this._closeNav();
  }

  onLibraryOptionSelected(event) {
    const selectedLibrary = event.option.value;

    if (selectedLibrary) {
      this._selectedLibraryWithId = selectedLibrary;
    }
    if (this.userName) {
      this.userName.nativeElement.focus();
    }
  }

  clearLibraryInput() {
    this.librariesControl.setValue('');
  }

  onInputFocus() {
    this.libraryInput.nativeElement.select();
  }

  private _closeNav() {
    this.forgotPasswordForm.get('userName').setValue('');
    this.closeNav();
  }

  get librariesControl() {
    return this.forgotPasswordForm.get('lid');
  }
}
